import { useTranslation } from 'react-i18next'
import i18n from '@/i18n'
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog'
import { Badge } from '@/components/ui/badge'
import { EnhancedAvatar } from '@/components/ui/enhanced-avatar'
import { Shield, User, CheckCircle, XCircle, Users } from 'lucide-react'
import { useTimezoneDate } from '@/hooks/useTimezoneDate'
import { getTimezoneInfo } from '@/utils/dynamicTimezone'
import { LANGUAGE_FLAGS, LANGUAGE_NAMES, capitalizeFirstLetter } from '@/lib/constants/languages'
import type { User as UserType } from '@/types/entities'
import { Button } from '@/components/ui/button'
import { useState } from 'react'
import { UserTeamsDialog } from './UserTeamsDialog'
import { useQuery } from '@tanstack/react-query'
import { locationsApi } from '@/features/locations/api'

interface UserViewDialogProps {
    open: boolean
    onOpenChange: (open: boolean) => void
    user: UserType | null
}

export function UserViewDialog({ open, onOpenChange, user }: UserViewDialogProps) {
    const { t } = useTranslation()
    const { formatDate } = useTimezoneDate()
    const [showTeamsDialog, setShowTeamsDialog] = useState(false)
    // Fetch locations to display location name
    const { data: locationsResponse } = useQuery({
        queryKey: ['locations'],
        queryFn: async () => {
            const response = await locationsApi.list({ page: 1, limit: 1000 })
            return response.data.data || []
        },
        enabled: open
    })
    const location = user?.location ? locationsResponse?.find(loc => loc.id === user.location) : null

    if (!user) {
        return null
    }

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="sm:max-w-[500px]">
                <DialogHeader>
                    <DialogTitle>{t('users.viewUser')}</DialogTitle>
                    <DialogDescription>
                        {t('userForm.viewUserDescription')}
                    </DialogDescription>
                </DialogHeader>

                <div className="space-y-6">
                    {/* User Info */}
                    <div className="flex items-center space-x-4">
                        <EnhancedAvatar
                            name={user.name}
                            avatarUrl={user.avatarUrl}
                            avatarColor={user.avatarColor}
                            userId={user.id}
                            size="xl"
                        />
                        <div>
                            <h3 className="text-lg font-semibold">{user.name}</h3>
                            <p className="text-muted-foreground">{user.email}</p>
                            <div className="mt-1 flex flex-wrap gap-1">
                                <Badge variant={user.isActive ? 'default' : 'secondary'}>
                                    {user.isActive ? t('users.active') : t('users.inactive')}
                                </Badge>
                                <Badge variant={user.standby ? 'secondary' : 'outline'}>
                                    {user.standby ? t('userForm.standbyUser') : t('userForm.permanentUser')}
                                </Badge>
                            </div>
                        </div>
                    </div>

                    {/* Roles */}
                    <div>
                        <label className="text-sm font-medium text-muted-foreground">{t('users.roles')}</label>
                        <div className="mt-2 flex flex-wrap gap-2">
                            {(user.roles ?? []).map((role) => (
                                <Badge
                                    key={role.id}
                                    variant={role.name === 'admin' ? 'default' : 'secondary'}
                                >
                                    {role.name === 'admin' ? (
                                        <Shield className="mr-1 h-3 w-3" />
                                    ) : (
                                        <User className="mr-1 h-3 w-3" />
                                    )}
                                    {i18n.exists(`roles.${role.name}`) ? t(`roles.${role.name}`) : role.name}
                                </Badge>
                            ))}
                        </div>
                    </div>

                    {/* Teams */}
                    <div>
                        <div className="flex items-center justify-between">
                            <label className="text-sm font-medium text-muted-foreground">{t('users.teams')}</label>
                            {(user.teams && user.teams.length > 0) && (
                                <Button
                                    variant="ghost"
                                    size="sm"
                                    className="h-6 w-6 p-0"
                                    onClick={() => setShowTeamsDialog(true)}
                                    title={t('users.viewTeams')}
                                >
                                    <Users className="h-3.5 w-3.5" />
                                </Button>
                            )}
                        </div>
                        <div className="mt-2">
                            {user.teams && user.teams.length > 0 ? (
                                <div className="flex flex-wrap gap-2">
                                    {user.teams.slice(0, 3).map((team) => (
                                        <Badge key={team.id} variant="secondary" className="text-xs">
                                            {team.teamName}
                                        </Badge>
                                    ))}
                                    {user.teams.length > 3 && (
                                        <Badge variant="secondary" className="text-xs">
                                            +{user.teams.length - 3} {t('users.more')}
                                        </Badge>
                                    )}
                                </div>
                            ) : (
                                <p className="text-sm text-muted-foreground">{t('users.noTeams')}</p>
                            )}
                        </div>
                    </div>

                    {/* Language */}
                    <div>
                        <label className="text-sm font-medium text-muted-foreground">{t('users.language')}</label>
                        <div className="mt-2 flex items-center space-x-2">
                            <span className="text-lg">
                                {LANGUAGE_FLAGS[user.language || 'en-US'] || LANGUAGE_FLAGS['en-US']}
                            </span>
                            <span className="text-sm">
                                {(() => {
                                    const userLanguage = user.language || 'en-US'
                                    let displayName = LANGUAGE_NAMES[userLanguage]
                                    if (!displayName && userLanguage) {
                                        try {
                                            const displayNames = new Intl.DisplayNames([userLanguage], { type: 'language' })
                                            const nativeName = displayNames.of(userLanguage.split('-')[0])
                                            displayName = nativeName ? capitalizeFirstLetter(nativeName) : userLanguage
                                        } catch {
                                            displayName = capitalizeFirstLetter(userLanguage)
                                        }
                                    }
                                    return displayName || 'English (US)'
                                })()}
                            </span>
                        </div>
                    </div>

                    {/* Timezone */}
                    <div>
                        <label className="text-sm font-medium text-muted-foreground">{t('users.timezone')}</label>
                        <div className="mt-2">
                            <div className="text-sm font-medium">{user.timezone || 'UTC'}</div>
                            {(() => {
                                const timezoneInfo = getTimezoneInfo(user.timezone || 'UTC')
                                return timezoneInfo && (
                                    <div className="text-xs text-muted-foreground">
                                        {timezoneInfo.currentOffset}
                                    </div>
                                )
                            })()}
                        </div>
                    </div>

                    {/* MFA Status */}
                    {/* Location */}
                    <div>
                        <label className="text-sm font-medium text-muted-foreground">{t('users.location')}</label>
                        <div className="mt-2">
                            <div className="text-sm font-medium">
                                {user.location && location ? location.description : t('common.notAvailable')}
                            </div>
                        </div>
                    </div>

                    <div>
                        <label className="text-sm font-medium text-muted-foreground">{t('userForm.twoFactorAuth')}</label>
                        <div className="mt-2 space-y-2">
                            <div className="flex items-center space-x-2">
                                {user.mfa?.totpEnabled ? (
                                    <CheckCircle className="h-4 w-4 text-green-500" />
                                ) : (
                                    <XCircle className="h-4 w-4 text-red-500" />
                                )}
                                <span className="text-sm">{t('userForm.authenticatorApp')}</span>
                            </div>
                            <div className="flex items-center space-x-2">
                                {user.mfa?.webauthnEnabled ? (
                                    <CheckCircle className="h-4 w-4 text-green-500" />
                                ) : (
                                    <XCircle className="h-4 w-4 text-red-500" />
                                )}
                                <span className="text-sm">{t('userForm.passkeys')}</span>
                            </div>
                        </div>
                    </div>

                    {/* Timestamps */}
                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label className="text-sm font-medium text-muted-foreground">{t('users.created')}</label>
                            <p className="text-sm">{formatDate(user.createdAt)}</p>
                        </div>
                        <div>
                            <label className="text-sm font-medium text-muted-foreground">{t('userForm.lastUpdated')}</label>
                            <p className="text-sm">{formatDate(user.updatedAt)}</p>
                        </div>
                    </div>
                </div>
            </DialogContent>

            <UserTeamsDialog
                open={showTeamsDialog}
                onOpenChange={setShowTeamsDialog}
                user={user}
            />
        </Dialog>
    )
}



